import { Modal, RadioGroup, RadioItem, Select } from '@/src/components/Antd';
import ModalFooter from '@/src/components/Antd/Modal/ModalFooter';
import NumberInput from '@/src/components/Antd/NumberInput/NumberInput';
import CommonHeader from '@/src/components/CommonHeader';
import {
  delPackage,
  getPackage,
  setPackage,
} from '@/src/request/settings/logistics/package';
import { unitSuffix, unitTypeList } from '@/src/tools/config';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  ConditionType,
  EnumPackageType,
  UnitType,
} from '@/src/types/common/common';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { CaretDownOutlined, InfoCircleFilled } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Checkbox, Form, Input, Spin } from 'antd';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import { breadcrumbData } from '../config';
import styles from './style.module.less';

const ShowContent = ({ value }: any) => {
  return <div>{value}</div>;
};

const permissions = () => {
  const add_edit_packages = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLogisticsPackages,
        operation: EnumOperation.add_edit_packages,
      }),
    ],
  });

  const view_packages_details = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLogisticsPackages,
        operation: EnumOperation.view_packages_details,
      }),
    ],
  });
  return { add_edit_packages, view_packages_details };
};

const LogisticsPackageSetting = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { add_edit_packages, view_packages_details } = permissions();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string | undefined }>();
  const [form] = Form.useForm();
  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);
  const [isDefault, setIsDefault] = React.useState<boolean>(false);
  const [modalContent, setModalContent] = React.useState<any>();
  const [visiable, setVisiable] = React.useState<boolean>(false);
  const [modalTitle, setModalTitle] = React.useState<any>();
  const { loading: setPackageLoading, run: _setPackage } = useRequest(
    (params) => setPackage(params, uuid),
    {
      manual: true,
      onSuccess(result) {
        history.push('/settings/logistics');
      },
    },
  );

  const { loading: getPackageLoadding, run: _getPackage } = useRequest(
    (uuid) => getPackage(uuid),
    {
      manual: true,
      ready: !!view_packages_details,
      onSuccess(result) {
        setIsDefault(result.asDefault);
        form.setFieldsValue({
          asDefault: result.asDefault,
          packageType: result.packageType,
          title: result.title,
          weight: result.weight,
          loadingMax: result.loadingMax,
          weightUnit: result.weightUnit,
          dimensionUnit: result.dimensionUnit,
          length: result.length,
          width: result.width,
          height: result.height,
          defaultEditable: result.defaultEditable,
        });
      },
    },
  );
  const { loading: delPackageLoading, run: _delPackage } = useRequest(
    (uuid) => delPackage(uuid),
    {
      manual: true,
      onSuccess(result) {
        history.push('/settings/logistics');
      },
    },
  );
  const packageBreadcrumbData = breadcrumbData({ getI18n }).concat({
    url: '',
    name: uuid
      ? getI18n('storeLogistics.package.editPackage')
      : getI18n('storeLogistics.package.addPackage'),
  });
  const showModal = () => {
    setVisiable(true);
  };
  const hideModal = () => {
    setVisiable(false);
  };
  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };
  const onFormSubmit = () => {
    form.validateFields().then((res) => {
      if (uuid) {
        res.uuid = uuid;
      }
      _setPackage(res);
    });
  };
  const goBackToList = () => {
    history.push(`/settings/logistics`);
  };
  const onDelete = () => {
    setModalTitle(
      <span>
        <InfoCircleFilled
          className={styles.headerIcon}
          style={{ color: '#ff695f' }}
        />
        <span>{getI18n('storeLogistics.package.deletePackage')}</span>
      </span>,
    );
    setModalContent(
      <div>
        <div className={styles.modalContent}>
          {getI18n('storeLogistics.package.deletePackageContent')}
        </div>
        <ModalFooter
          onCancel={() => {
            setVisiable(false);
          }}
          danger
          okText={getI18n('general.delete')}
          onOk={() => _delPackage(uuid)}
        />
      </div>,
    );
    showModal();
  };

  useEffect(() => {
    if (uuid) {
      _getPackage(uuid);
    }
  }, []);
  return (
    <div>
      <Spin
        spinning={setPackageLoading || getPackageLoadding || delPackageLoading}>
        <Form
          layout={'vertical'}
          form={form}
          className={styles.editForm}
          onValuesChange={onValuesChange}>
          <CommonHeader
            breadcrumbData={packageBreadcrumbData}
            onSaveDisable={onSaveDisable || !add_edit_packages}
            onSubmit={onFormSubmit}
            onDiscard={goBackToList}
            onGoBackClick={goBackToList}
            onDelete={uuid ? onDelete : null}
          />
          <div className={styles.packageContent}>
            {!uuid && (
              <div className={styles.itemBlock}>
                <div className={styles.header}>
                  <div className={styles.title}>
                    {getI18n('storeLogistics.package.packageType')}
                  </div>
                </div>
                <div className={styles.content}>
                  <Form.Item
                    name={'packageType'}
                    className={styles.radioBlock}
                    initialValue={EnumPackageType.Box}>
                    <RadioGroup
                      value={EnumPackageType.Box}
                      className={styles.radioGroup}>
                      <RadioItem
                        className={styles.radioItem}
                        value={EnumPackageType.Box}>
                        {getI18n('storeLogistics.package.box')}
                      </RadioItem>
                      <RadioItem
                        className={styles.radioItem}
                        value={EnumPackageType.Soft}>
                        {getI18n('storeLogistics.package.softText')}
                      </RadioItem>
                    </RadioGroup>
                  </Form.Item>
                </div>
              </div>
            )}
            <div className={styles.itemBlock}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('storeLogistics.package.sizeandWeight')}
                </div>
              </div>
              <div className={styles.content}>
                <div className={styles.formItemSize}>
                  <div className={styles.twoInOneRow}>
                    <Form.Item
                      className={styles.twoInOneRowItem}
                      name={'title'}
                      label={
                        <div className={styles.label}>
                          {getI18n('storeLogistics.package.packageTitle')}*
                        </div>
                      }
                      rules={[
                        {
                          required: true,
                          message: replaceI18nVariables(
                            getI18n('general.fieldisrequired'),
                            getI18n('storeLogistics.package.packageTitle'),
                          ),
                        },
                      ]}>
                      <Input size='large' maxLength={100} />
                    </Form.Item>
                    {uuid && (
                      <Form.Item
                        className={styles.twoInOneRowItem}
                        name={'packageType'}
                        label={
                          <div className={styles.label}>
                            {getI18n('storeLogistics.package.packageType')}
                          </div>
                        }>
                        <ShowContent />
                      </Form.Item>
                    )}
                  </div>
                  <div className={styles.hasBottomLine}></div>
                  <div className={styles.threeInOneRow}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(p, c) => p.weightUnit !== c.weightUnit}>
                      {({ getFieldsValue }) => {
                        const weightUnit = getFieldsValue(['weightUnit']);
                        const suffixText =
                          unitSuffix[weightUnit.weightUnit] || 'kg';
                        return (
                          <div className={styles.weightUnitSelect}>
                            <Form.Item
                              className={styles.threeInOneRowItem}
                              name={'weight'}
                              label={
                                <div className={styles.label}>
                                  {getI18n('storeLogistics.package.weight')}*
                                </div>
                              }
                              rules={[
                                {
                                  required: true,
                                  message: replaceI18nVariables(
                                    getI18n('general.fieldisrequired'),
                                    getI18n('storeLogistics.package.weight'),
                                  ),
                                },
                              ]}>
                              <NumberInput
                                size='large'
                                suffix={suffixText}
                                maxLength={100}
                                placeholder='0.00'
                              />
                            </Form.Item>

                            <Form.Item
                              className={styles.threeInOneRowItem}
                              name={'loadingMax'}
                              label={
                                <div className={styles.label}>
                                  {getI18n(
                                    'storeLogistics.package.maximumLoadingWeight',
                                  )}
                                </div>
                              }>
                              <NumberInput
                                size='large'
                                suffix={suffixText}
                                maxLength={100}
                                placeholder='0.00'
                              />
                            </Form.Item>
                          </div>
                        );
                      }}
                    </Form.Item>

                    <Form.Item
                      className={styles.threeInOneRowItem}
                      name={'weightUnit'}
                      label={
                        <div className={styles.label}>
                          {getI18n('storeLogistics.package.unitofWeight')}*
                        </div>
                      }
                      initialValue={UnitType.Kilogram}
                      rules={[
                        {
                          required: true,
                          message: replaceI18nVariables(
                            getI18n('general.fieldisrequired'),
                            getI18n('storeLogistics.package.unitofWeight'),
                          ),
                        },
                      ]}>
                      <Select
                        getPopupContainer={(trigger) => trigger}
                        optionsGroupList={unitTypeList[ConditionType.Weight]}
                        size='large'
                        suffixIcon={
                          <CaretDownOutlined className={styles.selectIcon} />
                        }
                      />
                    </Form.Item>
                  </div>
                  <div className={styles.hasBottomLine}></div>
                  <div className={styles.twoInOneRow}>
                    <Form.Item
                      className={styles.twoInOneRowItem}
                      name={'dimensionUnit'}
                      label={
                        <div className={styles.label}>
                          {getI18n('storeLogistics.package.unitofDimension')}*
                        </div>
                      }
                      initialValue={UnitType.Metre}
                      rules={[
                        {
                          required: true,
                          message: replaceI18nVariables(
                            getI18n('general.fieldisrequired'),
                            getI18n('storeLogistics.package.unitofDimension'),
                          ),
                        },
                      ]}>
                      <Select
                        size='large'
                        optionsGroupList={unitTypeList[ConditionType.Length]}
                        getPopupContainer={(trigger) => trigger}
                        suffixIcon={
                          <CaretDownOutlined className={styles.selectIcon} />
                        }
                      />
                    </Form.Item>
                  </div>
                  <Form.Item
                    noStyle
                    shouldUpdate={(p, c) =>
                      p.dimensionUnit !== c.dimensionUnit
                    }>
                    {({ getFieldsValue }) => {
                      const dimensionUnit = getFieldsValue(['dimensionUnit']);
                      const suffixText =
                        unitSuffix[dimensionUnit.dimensionUnit] || 'cm';
                      return (
                        <div className={styles.threeInOneRow}>
                          <Form.Item
                            className={styles.threeInOneRowItem}
                            name={'length'}
                            label={
                              <div className={styles.label}>
                                {getI18n('storeLogistics.package.length')}*
                              </div>
                            }
                            rules={[
                              {
                                required: true,
                                message: replaceI18nVariables(
                                  getI18n('general.fieldisrequired'),
                                  getI18n('storeLogistics.package.length'),
                                ),
                              },
                            ]}>
                            <NumberInput
                              size='large'
                              suffix={suffixText}
                              maxLength={100}
                              placeholder='0.00'
                            />
                          </Form.Item>
                          <Form.Item
                            className={styles.threeInOneRowItem}
                            name={'width'}
                            label={
                              <div className={styles.label}>
                                {getI18n('storeLogistics.package.width')}*
                              </div>
                            }
                            rules={[
                              {
                                required: true,
                                message: replaceI18nVariables(
                                  getI18n('general.fieldisrequired'),
                                  getI18n('storeLogistics.package.width'),
                                ),
                              },
                            ]}>
                            <NumberInput
                              size='large'
                              suffix={suffixText}
                              maxLength={100}
                              placeholder='0.00'
                            />
                          </Form.Item>
                          <Form.Item
                            className={styles.threeInOneRowItem}
                            name={'height'}
                            label={
                              <div className={styles.label}>
                                {getI18n('storeLogistics.package.height')}*
                              </div>
                            }
                            rules={[
                              {
                                required: true,
                                message: replaceI18nVariables(
                                  getI18n('general.fieldisrequired'),
                                  getI18n('storeLogistics.package.height'),
                                ),
                              },
                            ]}>
                            <NumberInput
                              size='large'
                              suffix={suffixText}
                              maxLength={100}
                              placeholder='0.00'
                            />
                          </Form.Item>
                        </div>
                      );
                    }}
                  </Form.Item>
                  <div className={styles.hasBottomLine}></div>
                  <div className={styles.defaultCheckbox}>
                    <Form.Item
                      name={'asDefault'}
                      valuePropName='checked'
                      initialValue={false}
                      extra={getI18n('storeLogistics.package.asDefaultExtra')}>
                      <Checkbox disabled={isDefault}>
                        <span className={styles.label}>
                          {getI18n(
                            'storeLogistics.package.setasdefaultpackage',
                          )}
                        </span>
                      </Checkbox>
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
      <Modal
        className={styles.courierAccountModal}
        width={600}
        visible={visiable}
        onCancel={hideModal}
        title={modalTitle}>
        {modalContent}
      </Modal>
    </div>
  );
};

export default LogisticsPackageSetting;
